import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const AdminGrievanceManagement = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [grievances, setGrievances] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
    highPriority: 0,
    mediumPriority: 0,
    lowPriority: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('manage');

  // User privileges state
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  // Submit grievance form states
  const [submitForm, setSubmitForm] = useState({
    category: 'maintenance',
    subject: '',
    description: '',
    priority: 'medium',
    file: null
  });
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchAdminData();
      fetchUserPrivileges();
      fetchGrievances();
    }
  }, [userInfo]);

  const fetchAdminData = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Error fetching admin data:', err);
    }
  };

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();
      
      console.log('Privileges response:', data);
      
      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        console.error('Failed to load privileges:', data.message);
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  // Group privileges by category
  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };


  // Check if user has access to this page
  useEffect(() => {
    if (!privilegesLoading && userPrivileges.length > 0) {
      console.log('🔍 Checking access for Grievances page...');
      console.log('User privileges:', userPrivileges.map(p => p.page_route));
      
      const hasAccess = userPrivileges.some(
        privilege => privilege.page_route === 'hostel-admin-grievances' || 
                     privilege.page_route === 'hostel-admin-paymentdetails' ||
                     privilege.page_route === 'hostel-admin-payment-details'
      );
      
      console.log('Has access to Grievances page:', hasAccess);
      
      if (!hasAccess) {
        alert('⚠️ Access Denied: You do not have permission to view this page.');
        if (onNavigate) {
          onNavigate('hostel-admin-dashboard');
        }
      }
    }
  }, [privilegesLoading, userPrivileges]);

  const fetchGrievances = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/grievances/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        const allGrievances = data.grievances || [];
        setGrievances(allGrievances);

        const statsData = {
          total: allGrievances.length,
          pending: allGrievances.filter(g => g.status === 'pending').length,
          inProgress: allGrievances.filter(g => g.status === 'in-progress').length,
          resolved: allGrievances.filter(g => g.status === 'resolved').length,
          rejected: allGrievances.filter(g => g.status === 'rejected').length,
          highPriority: allGrievances.filter(g => g.priority === 'high').length,
          mediumPriority: allGrievances.filter(g => g.priority === 'medium').length,
          lowPriority: allGrievances.filter(g => g.priority === 'low').length
        };
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching grievances:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (grievanceId, fileName) => {
    if (downloadingFile) return;
    
    try {
      setDownloadingFile(true);
      const response = await fetch(`${API_URL}/admin/grievance-file/${grievanceId}`);
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'grievance-file';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleUpdateGrievance = async () => {
    if (!selectedGrievance || !newStatus) {
      alert('Please select a status');
      return;
    }

    if (!adminResponse.trim()) {
      alert('Please provide a response');
      return;
    }

    try {
      setUpdating(true);
      const response = await fetch(`${API_URL}/admin/grievances/${selectedGrievance.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          admin_response: adminResponse,
          admin_id: userInfo.userId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Grievance updated successfully!');
        setShowModal(false);
        setSelectedGrievance(null);
        setAdminResponse('');
        setNewStatus('');
        fetchGrievances();
      } else {
        alert(data.message || 'Failed to update grievance');
      }
    } catch (err) {
      console.error('Error updating grievance:', err);
      alert('Failed to update grievance');
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmitGrievance = async (e) => {
    e.preventDefault();
    
    if (!submitForm.subject.trim() || !submitForm.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitLoading(true);
      
      const formData = new FormData();
      formData.append('category', submitForm.category);
      formData.append('subject', submitForm.subject);
      formData.append('description', submitForm.description);
      formData.append('priority', submitForm.priority);
      
      if (submitForm.file) {
        formData.append('file', submitForm.file);
      }

      const response = await fetch(`${API_URL}/student/grievances/${userInfo.userId}`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Grievance submitted successfully!');
        setShowSubmitModal(false);
        setSubmitForm({
          category: 'maintenance',
          subject: '',
          description: '',
          priority: 'medium',
          file: null
        });
        fetchGrievances();
        setActiveTab('manage');
      } else {
        alert(data.message || 'Failed to submit grievance');
      }
    } catch (err) {
      console.error('Error submitting grievance:', err);
      alert('Failed to submit grievance');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        alert('Please upload only JPG, PNG, or PDF files');
        e.target.value = '';
        return;
      }

      if (file.size > maxSize) {
        alert('File size should not exceed 5MB');
        e.target.value = '';
        return;
      }

      setSubmitForm({ ...submitForm, file });
    }
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      food: '🍽️',
      room: '🛏️',
      maintenance: '🔧',
      cleanliness: '🧹',
      security: '🔒',
      facilities: '🏢',
      staff: '👥',
      other: '📋'
    };
    return icons[category] || '📋';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f39c12',
      'in-progress': '#3498db',
      resolved: '#27ae60',
      rejected: '#e74c3c'
    };
    return colors[status] || '#95a5a6';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#27ae60',
      medium: '#f39c12',
      high: '#e74c3c'
    };
    return colors[priority] || '#95a5a6';
  };

  const filteredGrievances = grievances.filter(g => {
    const statusMatch = filterStatus === 'all' || g.status === filterStatus;
    const categoryMatch = filterCategory === 'all' || g.category === filterCategory;
    const priorityMatch = filterPriority === 'all' || g.priority === filterPriority;
    const searchMatch = !searchQuery || 
      g.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.grievance_number?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return statusMatch && categoryMatch && priorityMatch && searchMatch;
  });

  // Show loading state
  if (privilegesLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f2f8f7',
            borderTop: '4px solid #2b6777',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#2b6777',
            fontSize: '16px',
            fontWeight: '600'
          }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user has access to this page
  const hasPageAccess = userPrivileges.some(
    privilege => privilege.page_route === 'hostel-admin-grievances' || 
                 privilege.page_route === 'hostel-admin-paymentdetails' ||
                 privilege.page_route === 'hostel-admin-payment-details'
  );

  if (!hasPageAccess && !privilegesLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '48px 40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '24px'
          }}>🔒</div>
          <h2 style={{
            color: '#2b6777',
            fontSize: '24px',
            fontWeight: '800',
            marginBottom: '12px'
          }}>Access Denied</h2>
          <p style={{
            color: 'rgba(43,103,119,0.7)',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '32px',
            lineHeight: '1.6'
          }}>
            You do not have permission to access this page. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => onNavigate && onNavigate('hostel-admin-dashboard')}
            style={{
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(43,103,119,0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(43,103,119,0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(43,103,119,0.3)';
            }}
          >
            ← Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f2f8f7',
            borderTop: '4px solid #2b6777',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#2b6777',
            fontSize: '16px',
            fontWeight: '600'
          }}>Loading Grievances...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%);
          position: relative;
          overflow: hidden;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 280px;
          background: #f5f7f6;
          box-shadow: 4px 0 20px rgba(43,103,119,0.08);
          transform: translateX(-100%);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          display: flex;
          flex-direction: column;
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 24px 20px;
          background: linear-gradient(135deg, rgba(199,229,225,0.3) 0%, rgba(242,248,247,0.5) 100%);
          border-bottom: 1px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 16px;
        }

        .close-btn {
          background: rgba(43,103,119,0.1);
          border: none;
          cursor: pointer;
          font-size: 18px;
          color: #2b6777;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: rgba(43,103,119,0.2);
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          gap: 12px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(43,103,119,0.2);
          flex-shrink: 0;
        }

        .user-details-sidebar {
          flex: 1;
          min-width: 0;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role-sidebar {
          font-size: 12px;
          font-weight: 600;
          color: #52ab98;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 24px 16px;
          background: #f5f7f6;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 28px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          color: rgba(43,103,119,0.45);
          text-transform: uppercase;
          letter-spacing: 1.2px;
          margin-bottom: 12px;
          padding: 0 12px;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 12px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          margin-bottom: 6px;
          background: transparent;
        }

        .menu-item:hover {
          background: rgba(199,229,225,0.4);
          transform: translateX(4px);
        }

        .menu-item-active {
          background: rgba(199,229,225,0.5);
          box-shadow: 0 2px 8px rgba(82,171,152,0.15);
        }

        .menu-icon {
          font-size: 20px;
          width: 22px;
          text-align: center;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          font-size: 15px;
          font-weight: 600;
          color: #456c75;
          position: relative;
          z-index: 1;
          flex: 1;
        }

        .menu-item-active .menu-label {
          font-weight: 700;
          color: #2b6777;
          color: #2b6777;
        }

        .menu-glow {
          display: none;
        }

        .logout-section {
          padding: 16px;
          background: #f5f7f6;
        }

        .logout-btn {
          width: 100%;
          padding: 14px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #3d8179 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(43,103,119,0.25);
        }

        .logout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(43,103,119,0.35);
          background: linear-gradient(135deg, #3d8179 0%, #2b6777 100%);
        }

        .no-privileges-message {
          padding: 40px 20px;
          text-align: center;
          background: rgba(255,249,196,0.3);
          border-radius: 12px;
          margin: 20px;
        }

        .no-privileges-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .no-privileges-text {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 6px;
        }

        .no-privileges-subtext {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(2px);
          z-index: 999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s;
        }

        .overlay.show {
          opacity: 1;
          pointer-events: all;
        }

        .main-content {
          flex: 1;
          margin-left: 0;
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        @media (min-width: 1025px) {
          .sidebar {
            transform: translateX(0);
          }
          
          .main-content {
            margin-left: 280px;
            width: calc(100% - 280px);
          }

          .close-btn {
            display: none;
          }

          .overlay {
            display: none;
          }
        }

        .top-header {
          background: white;
          padding: 20px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 12px rgba(43,103,119,0.08);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          display: block;
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.5) 100%);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          font-size: 20px;
          cursor: pointer;
          color: #2b6777;
          transition: all 0.3s;
        }

        .menu-toggle:hover {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
        }

        @media (min-width: 1025px) {
          .menu-toggle {
            display: none;
          }
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
        }

        .header-greeting {
          font-size: 13px;
          font-weight: 600;
          color: rgba(43,103,119,0.6);
        }

        .header-name {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
          font-size: 18px;
          box-shadow: 0 4px 12px rgba(43,103,119,0.2);
        }

        .content-wrapper {
          flex: 1;
          overflow-y: auto;
          background: linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%);
        }

        .content-area {
          padding: 32px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .hero-section {
          background: white;
          padding: 32px;
          border-radius: 20px;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          margin-bottom: 32px;
        }

        .hero-title {
          font-size: 32px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .hero-subtitle {
          font-size: 16px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .tab-buttons {
          display: flex;
          gap: 16px;
          margin-bottom: 32px;
        }

        .tab-btn {
          flex: 1;
          padding: 16px 24px;
          background: white;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .tab-btn:hover {
          border-color: #52ab98;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(43,103,119,0.15);
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border-color: transparent;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .submit-card {
          background: white;
          padding: 32px;
          border-radius: 20px;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          animation: slideIn 0.5s ease-out;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .form-textarea {
          min-height: 120px;
          resize: vertical;
        }

        .file-upload {
          position: relative;
        }

        .file-upload input[type="file"] {
          display: none;
        }

        .file-upload-label {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px dashed rgba(82,171,152,0.4);
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
          cursor: pointer;
          transition: all 0.3s;
        }

        .file-upload-label:hover {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border-color: transparent;
        }

        .file-name {
          margin-top: 12px;
          padding: 12px;
          background: rgba(82,171,152,0.1);
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
        }

        .form-actions {
          display: flex;
          gap: 16px;
          margin-top: 32px;
        }

        .btn {
          flex: 1;
          padding: 16px 24px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-primary {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: rgba(82,171,152,0.1);
          color: #2b6777;
        }

        .btn-secondary:hover {
          background: rgba(82,171,152,0.2);
          transform: translateY(-2px);
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .metric-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          transition: all 0.3s;
          animation: slideIn 0.5s ease-out;
        }

        .metric-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.12);
        }

        .metric-icon {
          font-size: 32px;
          margin-bottom: 12px;
        }

        .metric-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .metric-value {
          font-size: 28px;
          font-weight: 800;
          color: #2b6777;
        }

        .filters-section {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          margin-bottom: 24px;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .filter-select,
        .search-input {
          padding: 12px 16px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          background: white;
          transition: all 0.3s;
        }

        .filter-select:focus,
        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .grievances-table-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          overflow: hidden;
        }

        .table-header {
          padding: 24px;
          border-bottom: 2px solid rgba(82,171,152,0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .table-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .table-count {
          font-size: 14px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          padding: 8px 16px;
          background: rgba(82,171,152,0.1);
          border-radius: 8px;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .grievances-table {
          width: 100%;
          border-collapse: collapse;
        }

        .grievances-table th {
          background: linear-gradient(135deg, rgba(242,248,247,0.5) 0%, rgba(199,229,225,0.3) 100%);
          padding: 16px;
          text-align: left;
          font-size: 12px;
          font-weight: 800;
          color: #2b6777;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        .grievances-table td {
          padding: 16px;
          border-bottom: 1px solid rgba(82,171,152,0.1);
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
        }

        .grievances-table tbody tr {
          transition: all 0.3s;
        }

        .grievances-table tbody tr:hover {
          background: rgba(242,248,247,0.5);
        }

        .status-badge,
        .priority-badge,
        .category-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 800;
          text-transform: capitalize;
        }

        .action-btn {
          padding: 8px 16px;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          color: #2b6777;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .action-btn:hover {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(43,103,119,0.2);
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .empty-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .empty-subtitle {
          font-size: 15px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 20px;
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 24px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .modal-close-btn {
          background: rgba(43,103,119,0.08);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          color: #2b6777;
          transition: all 0.3s;
        }

        .modal-close-btn:hover {
          background: rgba(43,103,119,0.15);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 24px;
        }

        .detail-section {
          margin-bottom: 20px;
        }

        .detail-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .detail-value {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
        }

        @media (max-width: 768px) {
          .content-area {
            padding: 20px;
          }

          .hero-title {
            font-size: 24px;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }

          .table-wrapper {
            overflow-x: scroll;
          }

          .grievances-table {
            min-width: 800px;
          }

          .tab-buttons {
            flex-direction: column;
          }

          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* SIDEBAR */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setSidebarOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin'}</div>
                <div className="user-role-sidebar">{dashboardData?.hostelName || 'Administrator'}</div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {/* Dynamically render menu sections based on user privileges */}
            
            {/* Overview Section */}
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Management Section */}
            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    active={privilege.page_route === 'hostel-admin-grievances'}
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Approval Section */}
            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Settings Section */}
            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Reports Section */}
            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Show message if no privileges */}
            {userPrivileges.length === 0 && !privilegesLoading && (
              <div className="no-privileges-message">
                <div className="no-privileges-icon">🔒</div>
                <div className="no-privileges-text">No Access Granted</div>
                <div className="no-privileges-subtext">Please contact administrator</div>
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        <div className={`overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

        {/* MAIN CONTENT */}
        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Grievance Management</div>
                <div className="header-name">Manage Student Concerns</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="content-area">
              <div className="hero-section">
                <h1 className="hero-title">Grievance Dashboard 📋</h1>
                <p className="hero-subtitle">
                  Review student grievances and submit concerns to super admin
                </p>
              </div>

              <div className="tab-buttons">
                <button 
                  className={`tab-btn ${activeTab === 'manage' ? 'active' : ''}`}
                  onClick={() => setActiveTab('manage')}
                >
                  <span>📊</span> Manage Grievances
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'submit' ? 'active' : ''}`}
                  onClick={() => setActiveTab('submit')}
                >
                  <span>➕</span> Submit to Super Admin
                </button>
              </div>

              {activeTab === 'submit' && (
                <div className="submit-card">
                  <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#2b6777', marginBottom: '24px' }}>
                    Submit Grievance to Super Admin
                  </h2>
                  <form onSubmit={handleSubmitGrievance}>
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <select
                        className="form-select"
                        value={submitForm.category}
                        onChange={(e) => setSubmitForm({ ...submitForm, category: e.target.value })}
                        required
                      >
                        <option value="food">Food</option>
                        <option value="room">Room</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="cleanliness">Cleanliness</option>
                        <option value="security">Security</option>
                        <option value="facilities">Facilities</option>
                        <option value="staff">Staff</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Priority</label>
                      <select
                        className="form-select"
                        value={submitForm.priority}
                        onChange={(e) => setSubmitForm({ ...submitForm, priority: e.target.value })}
                        required
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Subject *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={submitForm.subject}
                        onChange={(e) => setSubmitForm({ ...submitForm, subject: e.target.value })}
                        placeholder="Brief subject of the grievance"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Description *</label>
                      <textarea
                        className="form-textarea"
                        value={submitForm.description}
                        onChange={(e) => setSubmitForm({ ...submitForm, description: e.target.value })}
                        placeholder="Provide detailed information about the grievance..."
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Attach File (Optional)</label>
                      <div className="file-upload">
                        <input
                          type="file"
                          id="file-input"
                          accept="image/jpeg,image/png,image/jpg,application/pdf"
                          onChange={handleFileChange}
                        />
                        <label htmlFor="file-input" className="file-upload-label">
                          <span>📎</span> Choose File
                        </label>
                      </div>
                      {submitForm.file && (
                        <div className="file-name">
                          Selected: {submitForm.file.name}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: 'rgba(43,103,119,0.6)', marginTop: '8px' }}>
                        Supported: JPG, PNG, PDF (Max 5MB)
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setSubmitForm({
                            category: 'maintenance',
                            subject: '',
                            description: '',
                            priority: 'medium',
                            file: null
                          });
                        }}
                      >
                        Reset
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitLoading}
                      >
                        {submitLoading ? 'Submitting...' : '✓ Submit Grievance'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'manage' && (
                <>
                  <div className="metrics-grid">
                    <div className="metric-card">
                      <div className="metric-icon">📊</div>
                      <div className="metric-label">Total Grievances</div>
                      <div className="metric-value">{stats.total}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon">⏳</div>
                      <div className="metric-label">Pending</div>
                      <div className="metric-value">{stats.pending}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon">🔄</div>
                      <div className="metric-label">In Progress</div>
                      <div className="metric-value">{stats.inProgress}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon">✅</div>
                      <div className="metric-label">Resolved</div>
                      <div className="metric-value">{stats.resolved}</div>
                    </div>
                  </div>

                  <div className="filters-section">
                    <div className="filters-grid">
                      <div className="filter-group">
                        <label className="filter-label">Status</label>
                        <select
                          className="filter-select"
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      <div className="filter-group">
                        <label className="filter-label">Category</label>
                        <select
                          className="filter-select"
                          value={filterCategory}
                          onChange={(e) => setFilterCategory(e.target.value)}
                        >
                          <option value="all">All Categories</option>
                          <option value="food">Food</option>
                          <option value="room">Room</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="cleanliness">Cleanliness</option>
                          <option value="security">Security</option>
                          <option value="facilities">Facilities</option>
                          <option value="staff">Staff</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="filter-group">
                        <label className="filter-label">Priority</label>
                        <select
                          className="filter-select"
                          value={filterPriority}
                          onChange={(e) => setFilterPriority(e.target.value)}
                        >
                          <option value="all">All Priorities</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div className="filter-group">
                        <label className="filter-label">Search</label>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search by subject, student, grievance #..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grievances-table-card">
                    <div className="table-header">
                      <div className="table-title">All Grievances</div>
                      <div className="table-count">
                        {filteredGrievances.length} {filteredGrievances.length === 1 ? 'Grievance' : 'Grievances'}
                      </div>
                    </div>

                    {filteredGrievances.length > 0 ? (
                      <div className="table-wrapper">
                        <table className="grievances-table">
                          <thead>
                            <tr>
                              <th>Grievance #</th>
                              <th>Student</th>
                              <th>Category</th>
                              <th>Subject</th>
                              <th>Priority</th>
                              <th>Status</th>
                              <th>Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredGrievances.map((grievance) => (
                              <tr key={grievance.id}>
                                <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                                  {grievance.grievance_number || 'N/A'}
                                </td>
                                <td style={{ fontWeight: '700' }}>
                                  {grievance.student_name || 'Unknown'}
                                </td>
                                <td>
                                  <span className="category-badge" style={{ background: 'rgba(82,171,152,0.1)', color: '#2b6777' }}>
                                    {getCategoryIcon(grievance.category)} {grievance.category}
                                  </span>
                                </td>
                                <td>{grievance.subject || 'N/A'}</td>
                                <td>
                                  <span
                                    className="priority-badge"
                                    style={{
                                      background: `${getPriorityColor(grievance.priority)}20`,
                                      color: getPriorityColor(grievance.priority)
                                    }}
                                  >
                                    {grievance.priority}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className="status-badge"
                                    style={{
                                      background: `${getStatusColor(grievance.status)}20`,
                                      color: getStatusColor(grievance.status)
                                    }}
                                  >
                                    {grievance.status}
                                  </span>
                                </td>
                                <td>
                                  {grievance.created_at 
                                    ? new Date(grievance.created_at).toLocaleDateString() 
                                    : 'N/A'}
                                </td>
                                <td>
                                  <button
                                    className="action-btn"
                                    onClick={() => {
                                      setSelectedGrievance(grievance);
                                      setNewStatus(grievance.status);
                                      setAdminResponse(grievance.admin_response || '');
                                      setShowModal(true);
                                    }}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <div className="empty-icon">📋</div>
                        <div className="empty-title">No Grievances Found</div>
                        <div className="empty-subtitle">
                          {filterStatus !== 'all' || filterCategory !== 'all' || filterPriority !== 'all' || searchQuery
                            ? 'Try adjusting your filters'
                            : 'No grievances have been submitted yet'}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grievance Details Modal */}
      {showModal && selectedGrievance && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Grievance Details</h2>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-label">Grievance Number</div>
                <div className="detail-value" style={{ fontFamily: 'monospace' }}>
                  {selectedGrievance.grievance_number || 'N/A'}
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Student Name</div>
                <div className="detail-value">
                  {selectedGrievance.student_name || 'Unknown'}
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Category</div>
                <div className="detail-value">
                  {getCategoryIcon(selectedGrievance.category)} {selectedGrievance.category}
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Subject</div>
                <div className="detail-value">{selectedGrievance.subject}</div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Description</div>
                <div className="detail-value">{selectedGrievance.description}</div>
              </div>

              <div className="detail-section">
                <div className="detail-label">Priority</div>
                <span
                  className="priority-badge"
                  style={{
                    background: `${getPriorityColor(selectedGrievance.priority)}20`,
                    color: getPriorityColor(selectedGrievance.priority),
                    fontSize: '14px',
                    padding: '8px 16px'
                  }}
                >
                  {selectedGrievance.priority}
                </span>
              </div>

              <div className="detail-section">
                <div className="detail-label">Current Status</div>
                <span
                  className="status-badge"
                  style={{
                    background: `${getStatusColor(selectedGrievance.status)}20`,
                    color: getStatusColor(selectedGrievance.status),
                    fontSize: '14px',
                    padding: '8px 16px'
                  }}
                >
                  {selectedGrievance.status}
                </span>
              </div>

              {selectedGrievance.file_url && (
                <div className="detail-section">
                  <div className="detail-label">Attached File</div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleDownloadFile(selectedGrievance.id, selectedGrievance.file_url)}
                    disabled={downloadingFile}
                    style={{ marginTop: '8px' }}
                  >
                    {downloadingFile ? 'Downloading...' : '📎 Download File'}
                  </button>
                </div>
              )}

              <div className="detail-section">
                <div className="detail-label">Date Submitted</div>
                <div className="detail-value">
                  {selectedGrievance.created_at 
                    ? new Date(selectedGrievance.created_at).toLocaleString() 
                    : 'N/A'}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '24px' }}>
                <label className="form-label">Update Status</label>
                <select
                  className="form-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Admin Response</label>
                <textarea
                  className="form-textarea"
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Provide your response to this grievance..."
                  style={{ minHeight: '120px' }}
                />
              </div>

              <div className="form-actions" style={{ marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateGrievance}
                  disabled={updating}
                >
                  {updating ? 'Updating...' : '✓ Update Grievance'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminGrievanceManagement;